import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, Loader2, MessageCircle, Mail, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_URL =
    "https://wa.me/918303355977?text=Hi%20Innovated%20Global%2C%20I%27d%20like%20to%20discuss%20a%20project.";

const GREETING = {
    role: "assistant",
    content:
        "Hi! I'm Prompt Pilot, the Innovated Global AI assistant. Ask me anything about our Infor, digital marketing, software or AI services — or reach the team directly below.",
};

export const PromptPilot = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("chat");
    const [emailForm, setEmailForm] = useState({ name: "", work_email: "", message: "" });
    const sessionRef = useRef(
        localStorage.getItem("pp_session") ||
            (() => {
                const id = crypto.randomUUID();
                localStorage.setItem("pp_session", id);
                return id;
            })(),
    );
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, busy, mode, open]);

    const appendToLast = (delta) =>
        setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
            return copy;
        });

    const setLast = (content) =>
        setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content };
            return copy;
        });

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setBusy(true);
        try {
            const resp = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionRef.current, message: text }),
            });
            if (!resp.ok || !resp.body) throw new Error("bad response");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    const line = part.trim();
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).trim();
                    if (payload === "[DONE]") break;
                    try {
                        const obj = JSON.parse(payload);
                        if (obj.delta) appendToLast(obj.delta);
                        else if (obj.error) setLast(obj.error);
                    } catch {
                        /* partial chunk */
                    }
                }
            }
        } catch {
            setLast("Sorry — I couldn't reach the server just now. Please try again, or use WhatsApp / email below.");
        } finally {
            setBusy(false);
        }
    };

    const sendEmailForm = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            await axios.post(`${API}/enquiries`, {
                name: emailForm.name,
                work_email: emailForm.work_email,
                company: "",
                service_of_interest: "not-sure",
                message: emailForm.message,
            });
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: `Thanks, ${emailForm.name}! Your message has been emailed directly to the team — they'll reply to ${emailForm.work_email} shortly.`,
                },
            ]);
            setEmailForm({ name: "", work_email: "", message: "" });
            setMode("chat");
        } catch {
            setMessages((m) => [
                ...m,
                { role: "assistant", content: "That didn't go through — please check the details and try again." },
            ]);
            setMode("chat");
        } finally {
            setBusy(false);
        }
    };

    const resetChat = () => {
        sessionRef.current = (() => {
            const id = crypto.randomUUID();
            localStorage.setItem("pp_session", id);
            return id;
        })();
        setMessages([GREETING]);
    };

    return (
        <>
            <div className={`fixed bottom-5 right-5 z-[70] ${open ? "" : "pp-bob"}`}>
                {!open && <span aria-hidden="true" className="pp-ring absolute inset-0 rounded-full" />}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-label={open ? "Close Prompt Pilot chat" : "Open Prompt Pilot chat"}
                    data-testid="prompt-pilot-toggle"
                    className="relative flex h-14 w-14 items-center justify-center rounded-full transition-transform duration-300 hover:scale-110"
                    style={{
                        background: "linear-gradient(135deg, #0066FF, #7000FF 60%, #E6007E)",
                        boxShadow: "0 10px 34px -8px rgba(0,102,255,0.65), 0 0 22px -6px rgba(0,240,255,0.4)",
                    }}
                >
                    {open ? (
                        <X size={22} className="text-white" aria-hidden="true" />
                    ) : (
                        <img src="/assets/flower-mark.png" alt="" aria-hidden="true" className="header-flower-spin h-9 w-9 object-contain" />
                    )}
                </button>
            </div>

            {open && (
                <div
                    className="glass fixed bottom-[88px] right-5 z-[70] flex h-[70vh] max-h-[580px] w-[92vw] max-w-[400px] flex-col overflow-hidden rounded-3xl"
                    style={{ boxShadow: "0 30px 90px -30px rgba(0,0,0,0.8)" }}
                    role="dialog"
                    aria-label="Prompt Pilot AI assistant"
                    data-testid="prompt-pilot-panel"
                >
                    <div
                        className="flex items-center gap-3 px-5 py-4"
                        style={{ background: "linear-gradient(120deg, rgba(0,102,255,0.25), rgba(112,0,255,0.2), rgba(230,0,126,0.15))" }}
                    >
                        <img src="/assets/flower-mark.png" alt="" aria-hidden="true" className="header-flower-spin h-9 w-9 object-contain" />
                        <div className="flex-1">
                            <p className="text-sm font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>Prompt Pilot</p>
                            <p className="text-xs text-slate-400">AI assistant · replies instantly</p>
                        </div>
                        <button
                            type="button"
                            onClick={resetChat}
                            aria-label="Start a new conversation"
                            data-testid="prompt-pilot-reset"
                            className="rounded-full p-2 text-slate-400 transition-colors hover:text-white"
                        >
                            <RotateCcw size={15} aria-hidden="true" />
                        </button>
                    </div>

                    <div className="flex gap-2 border-b border-white/10 px-4 py-3">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="prompt-pilot-whatsapp-link"
                            className="flex flex-1 items-center justify-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs font-semibold text-emerald-300 transition-colors duration-200 hover:bg-emerald-400/20"
                        >
                            <MessageCircle size={14} aria-hidden="true" />
                            WhatsApp Us
                        </a>
                        <button
                            type="button"
                            onClick={() => setMode(mode === "email" ? "chat" : "email")}
                            data-testid="prompt-pilot-email-tab"
                            className="flex flex-1 items-center justify-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-xs font-semibold text-cyan-300 transition-colors duration-200 hover:bg-cyan-400/20"
                        >
                            <Mail size={14} aria-hidden="true" />
                            Email the Team
                        </button>
                    </div>

                    <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="prompt-pilot-messages">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                                        m.role === "user"
                                            ? "pp-bubble-user rounded-br-md text-white"
                                            : "rounded-bl-md border border-white/10 text-slate-200"
                                    }`}
                                    style={
                                        m.role === "user"
                                            ? { background: "linear-gradient(120deg, #0066FF, #7000FF)" }
                                            : { background: "rgba(255,255,255,0.04)" }
                                    }
                                >
                                    {m.content}
                                    {busy && m.role === "assistant" && i === messages.length - 1 && !m.content && (
                                        <Loader2 size={15} className="animate-spin text-slate-400" aria-label="Prompt Pilot is typing" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {mode === "email" ? (
                        <form onSubmit={sendEmailForm} className="space-y-2 border-t border-white/10 p-4" data-testid="prompt-pilot-email-form">
                            <input
                                type="text"
                                required
                                minLength={2}
                                placeholder="Your name"
                                aria-label="Your name"
                                className="field !py-2.5 text-xs"
                                value={emailForm.name}
                                onChange={(e) => setEmailForm((f) => ({ ...f, name: e.target.value }))}
                                data-testid="pp-email-name"
                            />
                            <input
                                type="email"
                                required
                                placeholder="Your email"
                                aria-label="Your email"
                                className="field !py-2.5 text-xs"
                                value={emailForm.work_email}
                                onChange={(e) => setEmailForm((f) => ({ ...f, work_email: e.target.value }))}
                                data-testid="pp-email-address"
                            />
                            <textarea
                                required
                                minLength={10}
                                rows={2}
                                placeholder="Your message to the team"
                                aria-label="Your message"
                                className="field resize-none !py-2.5 text-xs"
                                value={emailForm.message}
                                onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                                data-testid="pp-email-message"
                            />
                            <button type="submit" disabled={busy} className="btn-primary w-full !py-2.5 text-xs" data-testid="pp-email-submit">
                                {busy ? "Sending…" : "Send straight to the team's inbox"}
                            </button>
                        </form>
                    ) : (
                        <div className="flex items-center gap-2 border-t border-white/10 p-3">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") send();
                                }}
                                placeholder="Ask about our services…"
                                aria-label="Message Prompt Pilot"
                                maxLength={1000}
                                className="field flex-1 !rounded-full !py-2.5 text-sm"
                                data-testid="prompt-pilot-input"
                            />
                            <button
                                type="button"
                                onClick={send}
                                disabled={busy || !input.trim()}
                                aria-label="Send message"
                                data-testid="prompt-pilot-send"
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-200 hover:scale-105 disabled:opacity-40"
                                style={{ background: "linear-gradient(135deg, #0066FF, #7000FF)" }}
                            >
                                <Send size={16} aria-hidden="true" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
